// src/pages/Settings/Partenaires/EditPartenaireModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes, FaImage, FaGlobe, FaPhoneAlt, FaAt, FaMapMarkerAlt, FaListOl } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updatePartenaire } from "../../../services/partenairesServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Quill
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header","font","size",
  "bold","italic","underline","strike","blockquote",
  "color","background",
  "script",
  "list","bullet","indent",
  "link","image","video",
];

const EditPartenaireModal = ({ isActive, onClose, partenaireData, refreshPartenaires }) => {
  const [formData, setFormData] = useState({
    nom: "",
    photo_url: "",
    link_url: "",
    is_detail: false,
    tel: "",
    adresse: "",
    Mail: "",
    photo2_url: "",
    description2: "",
    photo3_url: "",
    description3: "",
    is_active: true,
    ordre: 0,
  });

  const defaultLogo = "/img/placeholder.png";
  const fallbackImg = "/img/user_avatar.jpg";
  const [logoPreview, setLogoPreview] = useState(defaultLogo);
  const [photo2Preview, setPhoto2Preview] = useState(defaultLogo);
  const [photo3Preview, setPhoto3Preview] = useState(defaultLogo);

  const dialogRef = useRef(null);

  // hydrate les champs dès qu'on reçoit partenaireData
  useEffect(() => {
    if (partenaireData) {
      const initial = {
        nom: partenaireData.nom ?? "",
        photo_url: partenaireData.photo_url ?? "",
        link_url: partenaireData.link_url ?? "",
        is_detail: !!partenaireData.is_detail,
        tel: partenaireData.tel ?? "",
        adresse: partenaireData.adresse ?? "",
        Mail: partenaireData.Mail ?? "",
        photo2_url: partenaireData.photo2_url ?? "",
        description2: partenaireData.description2 ?? "",
        photo3_url: partenaireData.photo3_url ?? "",
        description3: partenaireData.description3 ?? "",
        is_active: partenaireData.is_active ?? true,
        ordre: partenaireData.ordre ?? 0,
      };
      setFormData(initial);
      setLogoPreview(initial.photo_url?.trim() ? initial.photo_url : defaultLogo);
      setPhoto2Preview(initial.photo2_url?.trim() ? initial.photo2_url : defaultLogo);
      setPhoto3Preview(initial.photo3_url?.trim() ? initial.photo3_url : defaultLogo);
    }
  }, [partenaireData]);

  // lock du scroll de page + ESC pour fermer + focus
  useEffect(() => {
    if (!isActive) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isActive]);

  const handleClose = () => {
    onClose?.();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "photo_url") setLogoPreview(value.trim() ? value : defaultLogo);
      if (name === "photo2_url") setPhoto2Preview(value.trim() ? value : defaultLogo);
      if (name === "photo3_url") setPhoto3Preview(value.trim() ? value : defaultLogo);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partenaireData?.id) return;
    try {
      await updatePartenaire(partenaireData.id, formData);
      toast.success("Partenaire mis à jour avec succès");
      handleClose();
      refreshPartenaires?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du partenaire", error);
      toast.error("Erreur lors de la mise à jour du partenaire");
    }
  };

  if (!isActive) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier un partenaire"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h=[92dvh] sm:max-h-[92dvh]"
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Modifier le Partenaire {formData.nom ? `— ${formData.nom}` : ""}
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">Ajustez les informations et enregistrez.</p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="grid grid-cols-1 lg:grid-cols-[340px,1fr] gap-5 sm:gap-6">
            {/* Colonne gauche */}
            <div className="flex flex-col gap-4">
              <div className="relative w-full">
                <div className="relative h-28 sm:h-32 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center shadow">
                  <img
                    src={logoPreview}
                    alt="Logo du partenaire"
                    onError={(e) => { e.currentTarget.src = fallbackImg; }}
                    className="h-full w-full object-contain"
                  />
                  <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                    <FaImage className="text-[12px]" />
                  </span>
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Logo horizontal (PNG/JPG/WebP).</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">URL du logo principal</label>
                <input
                  type="url"
                  name="photo_url"
                  value={formData.photo_url}
                  onChange={handleChange}
                  placeholder="https://…/logo.png"
                  inputMode="url"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">URL (site web du partenaire)</label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaGlobe />
                  </span>
                  <input
                    type="url"
                    name="link_url"
                    value={formData.link_url}
                    onChange={handleChange}
                    placeholder="https://…"
                    inputMode="url"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50 px-3 py-2">
                  <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} className="accent-violet-500" />
                  <span className="text-sm text-slate-800 dark:text-slate-100">Afficher sur le site</span>
                </label>
                <label className="flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50 px-3 py-2">
                  <input type="checkbox" name="is_detail" checked={formData.is_detail} onChange={handleChange} className="accent-violet-500" />
                  <span className="text-sm text-slate-800 dark:text-slate-100">Page détail partenaire</span>
                </label>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Ordre d’affichage</label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaListOl />
                  </span>
                  <input
                    type="number"
                    name="ordre"
                    value={formData.ordre}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Plus petit = plus haut dans la liste.</p>
              </div>
            </div>

            {/* Colonne droite */}
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Nom du partenaire <span className="text-pink-600">*</span>
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  placeholder="Ex : Super Sponsor SAS"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Téléphone</label>
                  <div className="mt-1 relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                      <FaPhoneAlt />
                    </span>
                    <input
                      type="text"
                      name="tel"
                      value={formData.tel}
                      onChange={handleChange}
                      placeholder="05 56 …"
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Email</label>
                  <div className="mt-1 relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                      <FaAt />
                    </span>
                    <input
                      type="email"
                      name="Mail"
                      value={formData.Mail}
                      onChange={handleChange}
                      placeholder="contact@exemple.com"
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Adresse</label>
                <div className="mt-1 relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                    <FaMapMarkerAlt />
                  </span>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    placeholder="12 rue des Sponsors, 33000 Bordeaux"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Photos secondaires + descriptions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Bloc 2 */}
                <div className="flex flex-col gap-2">
                  <div className="relative h-28 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center shadow">
                    <img
                      src={photo2Preview}
                      alt="Aperçu photo 2"
                      onError={(e) => { e.currentTarget.src = fallbackImg; }}
                      className="h-full w-full object-contain"
                    />
                    <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                      <FaImage className="text-[12px]" />
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">URL photo 2</label>
                    <input
                      type="url"
                      name="photo2_url"
                      value={formData.photo2_url}
                      onChange={handleChange}
                      placeholder="https://…/visuel2.jpg"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Description 2</label>
                    <div className="mt-1 rounded-xl overflow-hidden border border-slate-300 dark:border-slate-700">
                      <ReactQuill
                        theme="snow"
                        value={formData.description2}
                        onChange={(value) => setFormData((prev) => ({ ...prev, description2: value }))}
                        modules={modules}
                        formats={formats}
                      />
                    </div>
                  </div>
                </div>

                {/* Bloc 3 */}
                <div className="flex flex-col gap-2">
                  <div className="relative h-28 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-2 ring-white dark:ring-slate-800 overflow-hidden flex items-center justify-center shadow">
                    <img
                      src={photo3Preview}
                      alt="Aperçu photo 3"
                      onError={(e) => { e.currentTarget.src = fallbackImg; }}
                      className="h-full w-full object-contain"
                    />
                    <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                      <FaImage className="text-[12px]" />
                    </span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">URL photo 3</label>
                    <input
                      type="url"
                      name="photo3_url"
                      value={formData.photo3_url}
                      onChange={handleChange}
                      placeholder="https://…/visuel3.jpg"
                      className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Description 3</label>
                    <div className="mt-1 rounded-xl overflow-hidden border border-slate-300 dark:border-slate-700">
                      <ReactQuill
                        theme="snow"
                        value={formData.description3}
                        onChange={(value) => setFormData((prev) => ({ ...prev, description3: value }))}
                        modules={modules}
                        formats={formats}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <Button
                text="Annuler"
                icon={FaTimes}
                onClick={handleClose}
                type="button"
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
              <Button text="Enregistrer" icon={FaSave} type="submit" className="!rounded-xl gap-2" />
            </div>
          </div>
        </form>
      </div>

      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb { background: rgba(148,163,184,.35); }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }

        .ql-container { min-height: 140px; }
        .ql-editor { min-height: 120px; }
      `}</style>
    </div>,
    document.body
  );
};

export default EditPartenaireModal;
